// RecebimentosViaBoletoPJQuery.jsx
import React from "react";
import { Badge } from "flowbite-react";
import { format, startOfMonth, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";

/* ===== Helpers ===== */
const brl = (v) =>
  (Number(v) || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const ymLabel = (d) => format(d, "MMM/yy", { locale: ptBR });

function clampInt(v, min = 1) {
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) && n >= min ? n : min;
}

/**
 * Array de meses (mais antigo -> mais recente).
 * - closedMonths = true: termina no mês anterior ao baseDate (mês fechado).
 * - closedMonths = false: inclui o mês de baseDate.
 */
function getClosedMonthsArray({
  baseDate = new Date(),
  closedMonths = true,
  months = 12,
}) {
  const n = clampInt(months, 1);
  const ref = closedMonths ? startOfMonth(baseDate) : baseDate;
  const latest = closedMonths ? subMonths(ref, 1) : ref;

  const arr = [];
  for (let i = n - 1; i >= 0; i--) {
    arr.push(subMonths(latest, i));
  }
  return arr;
}

/**
 * Divide um total entre N meses conforme a strategy.
 * - "even": partes iguais
 * - "frontloaded": mais no começo
 * - "backloaded": mais no fim (default)
 * - "weights": usa pesos fornecidos (length === n)
 */
function splitByStrategyN(total, strategy, weights, n) {
  const T = Number(total) || 0;
  const N = clampInt(n, 1);
  if (T <= 0) return Array.from({ length: N }, () => 0);

  let raw = [];

  if (strategy === "weights") {
    const w =
      Array.isArray(weights) && weights.length === N
        ? weights
        : Array.from({ length: N }, () => 1);
    const s = w.reduce((a, b) => a + Number(b || 0), 0) || 1;
    raw = w.map((x) => (Number(x || 0) / s) * T);
  } else if (strategy === "even") {
    raw = Array.from({ length: N }, () => T / N);
  } else if (strategy === "frontloaded") {
    // Pesos maiores no início (decrescente)
    const w = Array.from({ length: N }, (_, i) => N - i);
    const s = w.reduce((a, b) => a + b, 0);
    raw = w.map((x) => (x / s) * T);
  } else {
    // "backloaded" (default): pesos crescentes, mais no final
    const w = Array.from({ length: N }, (_, i) => i + 1);
    const s = w.reduce((a, b) => a + b, 0);
    raw = w.map((x) => (x / s) * T);
  }

  // Arredonda para 2 casas e ajusta a diferença no último mês
  const r = raw.map((v) => Math.round(v * 100) / 100);
  const diff = Math.round((T - r.reduce((a, b) => a + b, 0)) * 100) / 100;
  r[N - 1] = Math.round((r[N - 1] + diff) * 100) / 100;

  return r;
}

/* ===== Component ===== */
export default function RecebimentosViaBoletoPJQuery({
  data,
  months = 12, // << novo
  strategy = "even",
  weights,
  lastMonthOverride,
  closedMonths = true,
  baseDate,
  className,
}) {
  if (data?.indicador == null) {
    return (
      <div
        className={`w-full rounded-md border border-gray-200 p-3 mt-4 ${
          className || ""
        }`}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Recebimentos Via Boleto
            </h3>
          </div>
        </div>

        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 mt-4">
          <div className="flex gap-2">
            <span className="mt-1 h-2.5 w-2.5 flex-none rounded-full bg-amber-500" />
            <div className="text-sm leading-relaxed text-amber-900">
              Não há informações suficientes para exibir este indicador.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Total do período (assume que "indicador" é a soma dos 'months' meses)
  const totalPeriod = Number(String(data.indicador).replace(",", ".")) || 0;

  // Constrói vetor de meses (mais antigo -> mais recente)
  const monthsArr = getClosedMonthsArray({ baseDate, closedMonths, months });
  const oldest = monthsArr[0];
  const latest = monthsArr[monthsArr.length - 1];

  const periodLabel = `${ymLabel(oldest)} — ${ymLabel(latest)}`;
  const latestLabel = ymLabel(latest);

  // Divide o total pelo número de meses segundo a estratégia
  const split = splitByStrategyN(
    totalPeriod,
    strategy,
    weights,
    monthsArr.length
  );

  // Último mês
  const lastMonthValue =
    typeof lastMonthOverride === "number"
      ? lastMonthOverride
      : split[split.length - 1];
  const isEstimated = typeof lastMonthOverride !== "number";

  return (
    <div>
      <div
        className={`relative overflow-hidden shadow-sm rounded-md p-3 border border-gray-200 mt-4 bg-gray-50 ${
          className || ""
        }`}
      >
        <div className="relative flex flex-col gap-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <Badge color="indigo" className="uppercase">
                  Recebimentos
                </Badge>
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Período: {periodLabel}
                </span>
              </div>
              <h3 className="mt-2 text-base font-bold tracking-tight text-gray-900 dark:text-white">
                Recebimentos via Boleto — Últimos {monthsArr.length} meses
              </h3>
            </div>
          </div>

          <div className="grid gap-4 grid-cols-2">
            {/* Cartão total período */}
            <div className="rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Total ({monthsArr.length} meses)
                </span>
                <Badge color="success" className="text-2xs">
                  Consolidado
                </Badge>
              </div>
              <div className="mt-2 text-2xl font-bold text-blue-800 dark:text-white">
                {brl(totalPeriod)}
              </div>
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                Soma dos recebimentos no período {periodLabel}.
              </p>
            </div>

            {/* Cartão último mês */}
            <div className="rounded-2xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600 dark:text-gray-300">
                  Último mês ({latestLabel})
                </span>
                <div className="flex items-center gap-2">
                  {isEstimated ? (
                    <Badge color="gray" className="text-2xs">
                      Estimado
                    </Badge>
                  ) : (
                    <Badge color="gray" className="text-2xs">
                      Real
                    </Badge>
                  )}
                </div>
              </div>
              <div className="mt-2 text-2xl font-bold text-blue-800 dark:text-white">
                {brl(lastMonthValue)}
              </div>
              <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">
                Valor do mês mais recente do período.
              </p>
            </div>
          </div>

          {/* (Opcional) Lista por mês para validação visual
          <div className="mt-2 rounded-xl border border-gray-200 bg-white p-4">
            <h4 className="text-sm font-semibold text-gray-700">Distribuição por mês</h4>
            <ul className="mt-2 grid sm:grid-cols-2 md:grid-cols-3 gap-2">
              {monthsArr.map((d, idx) => (
                <li key={idx} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{ymLabel(d)}</span>
                  <span className="font-medium">{brl(split[idx])}</span>
                </li>
              ))}
            </ul>
          </div>
          */}
        </div>
      </div>
    </div>
  );
}
